import {
  CaseContentUpdatedContent,
  CaseCreatedContent,
  CasePropertyChangedContent,
  SupplementaryLinksActivityContent,
} from 'features/cases'
import {
  atomizeChangeset,
  diff,
  IAtomicChange,
  Operation,
  Options,
} from 'json-diff-ts'
import startCase from 'lodash/startCase'
import React from 'react'
import {
  Activity,
  GeckoCase,
  RolodexAuditLogJson,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types'

enum AssetModelSubPath {
  COMPONENTS = 'components',
  FEATURES = 'features',
  DATA = 'data',
  ROOT = 'root',
}

export const getRolodexJsonDiff = (
  oldValue: RolodexEntryJson,
  newValue: RolodexEntryJson
): IAtomicChange[] => {
  const diffOptions: Options = {
    keysToSkip: [
      'generation',
      'updated_at',
      'last_updated_by',
      'component_bounds',
      'scene_config',
    ],
  }
  const jsonDiff = diff(oldValue, newValue, diffOptions)
  return atomizeChangeset(jsonDiff)
}

const consolidateChanges = (changes: IAtomicChange[]): IAtomicChange[] => {
  const keySet = new Set(changes.map((change) => change.path))
  const consolidatedChanges: IAtomicChange[] = []
  keySet.forEach((key) => {
    const keyChanges = changes.filter((change) => change.path === key)
    if (keyChanges.length > 1) {
      const removedChanges = keyChanges.filter(
        (change) => change.type === Operation.REMOVE
      )
      const addedChanges = keyChanges.filter(
        (change) => change.type === Operation.ADD
      )

      if (removedChanges.length !== 1 || addedChanges.length !== 1) {
        console.warn(`Unable to determine the proper change order for ${key}`)
      }

      consolidatedChanges.push({
        type: Operation.UPDATE,
        key: addedChanges[0].key,
        oldValue: removedChanges[0].value,
        value: addedChanges[0].value,
        path: addedChanges[0].path,
        valueType: addedChanges[0].valueType,
      })
    } else {
      consolidatedChanges.push(keyChanges[0])
    }
  })
  return consolidatedChanges
}

const getValueFromNestedJSON = (
  jsonObject: Record<string, unknown>,
  keys: string[]
) => {
  return keys.reduce<unknown>((obj, key: string): unknown => {
    if (obj && typeof obj === 'object' && key in obj) {
      // @ts-expect-error -- obj[key] could have any type, but that's OK
      return obj[key]
    } else {
      return undefined
    }
  }, jsonObject)
}

const getSubPath = (
  pathKeys: string[],
  subPathType: AssetModelSubPath,
  includeID: boolean = true
): string[] => {
  /* Get the sub path from a list of keys, return the root if the sub-path type isn't found */
  const indexOfSubPathType = pathKeys.indexOf(subPathType)
  const indexIncrement = includeID ? 2 : 1
  return pathKeys.slice(0, indexOfSubPathType + indexIncrement)
}

const getAssetModelSubPathType = (pathKeys: string[]): AssetModelSubPath => {
  const indexOfComponents = pathKeys.indexOf(AssetModelSubPath.COMPONENTS)
  const indexOfFeatures = pathKeys.indexOf(AssetModelSubPath.FEATURES)
  const indexOfData = pathKeys.indexOf(AssetModelSubPath.DATA)
  if (indexOfFeatures !== -1) {
    return AssetModelSubPath.FEATURES
  } else if (indexOfComponents !== -1) {
    return AssetModelSubPath.COMPONENTS
  } else if (indexOfData !== -1) {
    return AssetModelSubPath.DATA
  } else {
    return AssetModelSubPath.ROOT
  }
}

export const deconstructAuditLog = (
  auditLog: RolodexAuditLogJson
): Activity[] => {
  if (!auditLog.old_value) {
    const assetType = auditLog.new_value.type
    const entityName = startCase(assetType)
    return [
      {
        key: 'entity-created',
        title: `${entityName} Created`,
        body: JSON.stringify(auditLog.new_value, null, 2),
        data: auditLog.new_value,
      },
    ]
  }

  const diff = getRolodexJsonDiff(auditLog.old_value, auditLog.new_value)
  const consolidatedChanges = consolidateChanges(diff)

  const entryType = auditLog.new_value.type
  switch (entryType) {
    case RolodexEntryType.ASSET_MODEL:
      return deconstructAssetModelAuditLog(
        consolidatedChanges,
        auditLog.new_value
      )
    case RolodexEntryType.UNIT:
      return deconstructUnitAuditLog(consolidatedChanges)
    case RolodexEntryType.COMPONENT:
      return deconstructComponentAuditLog(consolidatedChanges)
    case RolodexEntryType.EXTENSION_CONFIG:
      return deconstructExtensionConfigAuditLog(consolidatedChanges)
    case RolodexEntryType.COMPONENT_DATASET:
      return deconstructCDSAuditLog(consolidatedChanges)
    case RolodexEntryType.VALUE_STORY:
      return deconstructValueStoryAuditLog(consolidatedChanges)
    case RolodexEntryType.CASE:
      return deconstructCaseAuditLog(
        consolidatedChanges,
        new GeckoCase(auditLog.new_value)
      )
    default:
      return [
        {
          key: 'invalid-entry-type',
          title: 'No matching deconstructor',
          body: `No function was provided in deconstructAuditLog to generate Activity objects from ${entryType} entries`,
        },
      ]
  }
}

const deconstructAssetModelAuditLog = (
  changes: IAtomicChange[],
  assetModelJSON: RolodexEntryJson
): Activity[] => {
  // Since horizontal and vertical welds are currently auto-calculated, the logs would become cluttered if we
  // displayed them all being removed and re-added
  const filteredChanges = changes.filter((change) => {
    return !(
      change.valueType === 'Object' &&
      (change.value.type === 'horizontal_weld' ||
        change.value.type === 'vertical_weld')
    )
  })
  return filteredChanges.map((change) => {
    const fieldDescriptionSafe = change.value?.name ?? ''
    const nestedKeys = change.path.split('.')
    switch (change.type) {
      case Operation.ADD: {
        let addedName, fieldDescription
        switch (getAssetModelSubPathType(nestedKeys)) {
          case AssetModelSubPath.COMPONENTS:
            addedName = 'Component Added'
            fieldDescription = fieldDescriptionSafe
            break
          case AssetModelSubPath.FEATURES:
            // addedName = 'Feature Added'
            // fieldDescription = change.value.name ?? ''
            break
          default:
            addedName = startCase(change.key)
            fieldDescription = fieldDescriptionSafe
            break
        }
        return {
          key: change.path,
          title: addedName,
          body: fieldDescription,
        }
      }

      case Operation.UPDATE: {
        const pathKeys = change.path.split('.').slice(1)
        const subPathType = getAssetModelSubPathType(pathKeys)
        const fieldName = nestedKeys.at(-1)
        const oldValue = change.oldValue
        const newValue = change.value
        if (subPathType === AssetModelSubPath.ROOT) {
          return {
            key: change.path,
            title: 'Asset Model Created',
            body: '',
          }
        } else if (subPathType === AssetModelSubPath.COMPONENTS) {
          const componentPath = getSubPath(
            pathKeys,
            AssetModelSubPath.COMPONENTS
          )
          const componentName = getValueFromNestedJSON(
            assetModelJSON,
            componentPath.concat('name')
          )
          return {
            key: change.path,
            title: `${fieldName ? startCase(fieldName) : 'Component Updated'} ${componentName ? `(${componentName})` : ''}`,
            body: `${oldValue ? oldValue : '(None)'} --> ${newValue ? newValue : '(None)'}`,
          }
        } else if (subPathType === AssetModelSubPath.FEATURES) {
          const featurePath = pathKeys.slice(0, -2)
          const featureName = getValueFromNestedJSON(
            assetModelJSON,
            featurePath.concat('name')
          )
          return {
            key: change.path,
            title: `${fieldName ? startCase(fieldName) : 'Feature Updated'} ${featureName ? `(${featureName})` : ''}`,
            body: `${oldValue ? oldValue : '(None)'} --> ${newValue ? newValue : '(None)'}`,
          }
        } else {
          return {
            key: change.path,
            title: fieldName ? startCase(fieldName) : 'Field Updated',
            body: `${oldValue ? oldValue : '(None)'} --> ${newValue ? newValue : '(None)'}`,
          }
        }
      }

      case Operation.REMOVE: {
        let removedName, fieldDescription
        switch (getAssetModelSubPathType(nestedKeys)) {
          case AssetModelSubPath.COMPONENTS:
            removedName = 'Component Removed'
            fieldDescription = fieldDescriptionSafe
            break
          case AssetModelSubPath.FEATURES:
            // removedName = 'Feature Removed'
            // fieldDescription = change.value.name ?? ''
            break
          default:
            removedName = startCase(change.key)
            fieldDescription = fieldDescriptionSafe
            break
        }
        return {
          key: change.path,
          title: removedName,
          body: fieldDescription,
        }
      }
    }
  })
}

const deconstructUnitAuditLog = (changes: IAtomicChange[]): Activity[] => {
  return changes.map((change) => {
    const nestedKeys = change.path.split('.')
    switch (change.type) {
      case Operation.ADD: {
        const addedName = startCase(nestedKeys.at(-1))
        const addedDesciption = JSON.stringify(change.value)
        return {
          key: change.path,
          title: addedName,
          body: addedDesciption,
        }
      }

      case Operation.UPDATE: {
        const fieldName = startCase(nestedKeys.at(-1))
        const oldValue = change.oldValue
        const newValue = change.value
        if (change.path === '$.$root') {
          return {
            key: change.path,
            title: 'Unit Created',
            body: '',
          }
        }
        return {
          key: change.path,
          title: fieldName ? startCase(fieldName) : 'Field Updated',
          body: `${oldValue ? oldValue : '(None)'} --> ${newValue ? newValue : '(None)'}`,
        }
      }

      case Operation.REMOVE: {
        const removedName = startCase(nestedKeys.at(-1))
        return {
          key: change.path,
          title: removedName,
          body: '(Removed)',
        }
      }
    }
  })
}

const deconstructComponentAuditLog = (changes: IAtomicChange[]): Activity[] => {
  return changes.map((change) => {
    const nestedKeys = change.path.split('.')
    switch (change.type) {
      case Operation.ADD: {
        const addedName = startCase(nestedKeys.at(-1))
        const addedDesciption = JSON.stringify(change.value)
        return {
          key: change.path,
          title: addedName,
          body: addedDesciption,
        }
      }

      case Operation.UPDATE: {
        const fieldName = startCase(nestedKeys.at(-1))
        const oldValue = change.oldValue
        const newValue = change.value
        if (change.path === '$.$root') {
          return {
            key: change.path,
            title: 'Component Created',
            body: '',
          }
        }
        return {
          key: change.path,
          title: fieldName ? startCase(fieldName) : 'Field Updated',
          body: `${oldValue ? oldValue : '(None)'} --> ${newValue ? newValue : '(None)'}`,
        }
      }

      case Operation.REMOVE: {
        const removedName = startCase(nestedKeys.at(-1))
        return {
          key: change.path,
          title: removedName,
          body: '(Removed)',
        }
      }
    }
  })
}

const deconstructExtensionConfigAuditLog = (
  changes: IAtomicChange[]
): Activity[] => {
  return changes.map((change) => {
    const nestedKeys = change.path.split('.')
    switch (change.type) {
      case Operation.ADD: {
        const addedName = startCase(nestedKeys.at(-1))
        const addedDescription = JSON.stringify(change.value)
        return {
          key: change.path,
          title: `Field Added: ${addedName}`,
          body: addedDescription,
        }
      }
      case Operation.UPDATE: {
        const fieldName = startCase(nestedKeys.at(-1))
        const oldValue = change.oldValue
        const newValue = change.value
        return {
          key: change.path,
          title: `Field Updated: ${fieldName}`,
          body: `${oldValue ? JSON.stringify(oldValue) : '(None)'} --> ${newValue ? JSON.stringify(newValue) : '(None)'}`,
        }
      }
      case Operation.REMOVE: {
        const removedName = startCase(nestedKeys.at(-1))
        return {
          key: change.path,
          title: `Field Removed: ${removedName}`,
          body: '(Removed)',
        }
      }
    }
  })
}

const deconstructCDSAuditLog = (changes: IAtomicChange[]): Activity[] => {
  return changes.map((change) => {
    const nestedKeys = change.path.split('.')
    switch (change.type) {
      case Operation.ADD: {
        const addedName =
          startCase(nestedKeys.at(-2)) +
          ' > ' +
          startCase(nestedKeys.at(-1)) +
          ' Added'
        const addedDesciption = JSON.stringify(change.value)
        return {
          key: change.path,
          title: addedName,
          body: addedDesciption,
        }
      }

      case Operation.UPDATE: {
        const fieldName = startCase(nestedKeys.at(-1)) + ' Updated'
        const oldValue = change.oldValue
        const newValue = change.value
        return {
          key: change.path,
          title: fieldName ? startCase(fieldName) : 'Field Updated',
          body: `${oldValue ? oldValue : '(None)'} --> ${newValue ? newValue : '(None)'}`,
        }
      }

      case Operation.REMOVE: {
        const removedName = startCase(nestedKeys.at(-1)) + ' Removed'
        return {
          key: change.path,
          title: removedName,
          body: '(Removed)',
        }
      }
    }
  })
}

const deconstructValueStoryAuditLog = (
  changes: IAtomicChange[]
): Activity[] => {
  return changes.map((change) => {
    const nestedKeys = change.path.split('.')
    switch (change.type) {
      case Operation.ADD: {
        const addedName = startCase(nestedKeys.at(-1))
        const addedDescription = JSON.stringify(change.value)
        return {
          key: change.path,
          title: addedName,
          body: addedDescription,
        }
      }

      case Operation.UPDATE: {
        const fieldName = startCase(nestedKeys.at(-1))
        const oldValue = change.oldValue
        const newValue = change.value
        if (change.path === '$.$root') {
          return {
            key: change.path,
            title: 'Value Story Created',
            body: '',
          }
        }

        // Handle specific value story fields with better formatting
        switch (nestedKeys.at(-1)) {
          case 'title':
            return {
              key: change.path,
              title: 'Title Updated',
              body: `"${oldValue || '(None)'}" → "${newValue || '(None)'}"`,
            }
          case 'content':
            return {
              key: change.path,
              title: 'Content Updated',
              body: 'Story content was modified',
            }
          case 'value_status':
            return {
              key: change.path,
              title: 'Value Status Updated',
              body: `${oldValue || '(None)'} → ${newValue || '(None)'}`,
            }
          case 'action_status':
            return {
              key: change.path,
              title: 'Action Status Updated',
              body: `${oldValue || '(None)'} → ${newValue || '(None)'}`,
            }
          case 'value_created':
            return {
              key: change.path,
              title: 'Value Created Updated',
              body: `${oldValue || '(None)'} → ${newValue || '(None)'}`,
            }
          case 'value_unit':
            return {
              key: change.path,
              title: 'Value Unit Updated',
              body: `${oldValue || '(None)'} → ${newValue || '(None)'}`,
            }
          case 'date':
            return {
              key: change.path,
              title: 'Date Updated',
              body: `${oldValue || '(None)'} → ${newValue || '(None)'}`,
            }
          case 'units':
            return {
              key: change.path,
              title: 'Assets Updated',
              body: `${Array.isArray(oldValue) ? oldValue.join(', ') || '(None)' : oldValue || '(None)'} → ${Array.isArray(newValue) ? newValue.join(', ') || '(None)' : newValue || '(None)'}`,
            }
          case 'systems':
            return {
              key: change.path,
              title: 'Systems Updated',
              body: `${Array.isArray(oldValue) ? oldValue.join(', ') || '(None)' : oldValue || '(None)'} → ${Array.isArray(newValue) ? newValue.join(', ') || '(None)' : newValue || '(None)'}`,
            }
          default:
            return {
              key: change.path,
              title: fieldName ? startCase(fieldName) : 'Field Updated',
              body: `${oldValue ? oldValue : '(None)'} → ${newValue ? newValue : '(None)'}`,
            }
        }
      }

      case Operation.REMOVE: {
        const removedName = startCase(nestedKeys.at(-1))
        return {
          key: change.path,
          title: removedName,
          body: '(Removed)',
        }
      }
    }
  })
}

const deconstructCaseAuditLog = (
  changes: IAtomicChange[],
  caseJSON: GeckoCase
): Activity[] => {
  return changes.map((change) => {
    const nestedKeys = change.path.split('.')
    const fieldName = nestedKeys.at(-1)

    if (change.path.includes('supplementary_links')) {
      switch (change.type) {
        case Operation.ADD:
          return {
            key: change.path,
            title: 'Supplementary Links Updated',
            body: (
              <SupplementaryLinksActivityContent
                addedIds={[change.value]}
                removedIds={[]}
              />
            ),
          }
        case Operation.REMOVE:
          return {
            key: change.path,
            title: 'Supplementary Links Updated',
            body: (
              <SupplementaryLinksActivityContent
                addedIds={[]}
                removedIds={[change.value]}
              />
            ),
          }
        case Operation.UPDATE: {
          const oldLinks = Array.isArray(change.oldValue) ? change.oldValue : []
          const newLinks = Array.isArray(change.value) ? change.value : []
          const addedIds = newLinks.filter((id) => !oldLinks.includes(id))
          const removedIds = oldLinks.filter((id) => !newLinks.includes(id))

          return {
            key: change.path,
            title: 'Supplementary Links Updated',
            body: (
              <SupplementaryLinksActivityContent
                addedIds={addedIds}
                removedIds={removedIds}
              />
            ),
          }
        }
      }
    }

    switch (change.type) {
      case Operation.ADD: {
        const addedName = startCase(fieldName)
        const addedDescription = JSON.stringify(change.value)
        return {
          key: change.path,
          title: `${addedName} Added`,
          body: addedDescription,
        }
      }

      case Operation.UPDATE: {
        const oldValue = change.oldValue
        const newValue = change.value

        if (change.path === '$.$root') {
          return {
            key: change.path,
            title: 'Case Created',
            body: (
              <CaseCreatedContent
                caseTitle={caseJSON.data?.case_title}
                caseType={caseJSON.data?.case_type}
              />
            ),
          }
        }

        // Handle case_metadata changes
        if (change.path.includes('case_metadata')) {
          const metadataKey = fieldName
          return {
            key: change.path,
            title: `${startCase(metadataKey)} Updated`,
            body: (
              <CasePropertyChangedContent
                field={`case_metadata.${metadataKey}`}
                oldStatus={String(oldValue || '(None)')}
                newStatus={String(newValue || '(None)')}
              />
            ),
          }
        }

        switch (fieldName) {
          case 'case_title':
            return {
              key: change.path,
              title: 'Title Updated',
              body: (
                <CasePropertyChangedContent
                  field='case_title'
                  oldStatus={oldValue as string}
                  newStatus={newValue as string}
                />
              ),
            }
          case 'case_type':
            return {
              key: change.path,
              title: 'Type Updated',
              body: (
                <CasePropertyChangedContent
                  field='case_type'
                  oldStatus={oldValue as string}
                  newStatus={newValue as string}
                />
              ),
            }
          case 'case_status':
            return {
              key: change.path,
              title: 'Status Updated',
              body: (
                <CasePropertyChangedContent
                  field='case_status'
                  oldStatus={oldValue as string}
                  newStatus={newValue as string}
                />
              ),
            }
          case 'is_published':
            return {
              key: change.path,
              title: 'Publication Status Updated',
              body: (
                <CasePropertyChangedContent
                  field='is_published'
                  oldStatus={oldValue ? 'Published' : 'Not Published'}
                  newStatus={newValue ? 'Published' : 'Not Published'}
                />
              ),
            }
          case 'content':
            return {
              key: change.path,
              title: 'Content Updated',
              body: <CaseContentUpdatedContent />,
            }
          default:
            // Fallback for other fields
            return {
              key: change.path,
              title: `${startCase(fieldName)} Updated`,
              body: `${oldValue || '(None)'} → ${newValue || '(None)'}`,
            }
        }
      }

      case Operation.REMOVE: {
        const removedName = startCase(fieldName)
        return {
          key: change.path,
          title: `${removedName} Removed`,
          body: '(Removed)',
        }
      }
    }
  })
}
